import { useComprehensiveTopNav, useWindowResize } from 'hooks';
import { routes } from 'lib/api';
import { Button, Image, LoadingIndicator, Typography } from 'lib/components';
import { JobStatus } from 'lib/util';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
	actions,
	RequestManager,
	useAppDispatch,
	useSelectAuthorization,
	useSelectJobs,
	useSelectTopNavState,
	useSelectRequest,
} from 'store';
import MapView from '../../lib/components/MapView';
import ActiveJobsTable from './ActiveJobsTable';
import classes from './index.module.scss';
import JobRequestTable from './JobRequestTable';
import QuickAccessItem from './QuickAccessItem';

/**
 * Main dashboard component that displays job statistics, active jobs, and job requests
 * Includes a map view that can be toggled on/off and responsive layout for different device sizes
 */
const DashboardScreen = () => {
	// Get current window width for responsive design adjustments
	const { width } = useWindowResize();
	const { push } = useHistory();
	// Flag to determine if we're on a mobile device (width <= 500px)
	const isSmallerDevice = width <= 500;

	const dispatch = useAppDispatch();
	// Track active API requests
	const { active } = useSelectRequest();
	// Permission check for job listing capability
	const { canListJob } = useSelectAuthorization();
	// Controls visibility of the map component
	const { mapToggleVisible } = useSelectTopNavState();
	// All jobs data from the Redux store
	const jobs = useSelectJobs();

	// Tracks whether initial data has been loaded
	const [isDataFetched, setIsDataFetched] = useState(false);

	/**
	 * Fetch jobs data on component mount if user has permission
	 * Otherwise, mark data as fetched to avoid loading indicator
	 */
	useEffect(() => {
		if (canListJob) {
			dispatch(actions.fetchJobs());
		} else {
			setIsDataFetched(true);
		}
	}, []);

	/**
	 * Monitor API requests to track when job data has finished loading
	 * Uses RequestManager to check status of GET requests to the JOB endpoint
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (RM.req(routes.JOB, 'GET')) {
			if (RM.succeeded()) {
				setIsDataFetched(true);
			}
		}
	}, [active]);

	// Set up the top navigation bar with title and map toggle button
	useComprehensiveTopNav('Dashboard', { mapToggleVisible: true });

	// Center map on hospital locations from jobs (location is [lng, lat]).
	// Fall back to NYC only when no valid coordinates exist yet.
	const mapCenter = (() => {
		const points = jobs.list
			.map((job) => job.hospital?.address?.location)
			.filter(
				(loc): loc is number[] =>
					Array.isArray(loc) &&
					loc.length >= 2 &&
					Number.isFinite(loc[0]) &&
					Number.isFinite(loc[1])
			);

		if (points.length === 0) {
			return { longitude: -74, latitude: 40.72 };
		}

		const longitude = points.reduce((sum, loc) => sum + loc[0], 0) / points.length;
		const latitude = points.reduce((sum, loc) => sum + loc[1], 0) / points.length;
		return { longitude, latitude };
	})();

	// Show loading indicator while data is being fetched
	if (!isDataFetched) return <LoadingIndicator />;

	return (
		<div className={classes['dashboard']}>
			{mapToggleVisible && (
				<div className={classes['dashboard-map']}>
					<MapView longitude={mapCenter.longitude} latitude={mapCenter.latitude} />
				</div>
			)}
			{canListJob && (
				<div className={classes['dashboard-group']}>
					<div className={classes['dashboard-quickAccess']}>
						<Typography className={classes['dashboard-quickAccess-heading']} variant="h4">
							Quick Access
						</Typography>
						<div className={classes['dashboard-quickAccess-list']}>
							{/* Display count of jobs without invoices */}
							<QuickAccessItem
								title="Pending Invoices"
								value={jobs.list.filter((job) => (job.invoice ? false : true)).length}
								isSmallerDevice={isSmallerDevice}
							/>
							{/* Display count of jobs without assigned nurses */}
							<QuickAccessItem
								title="Unassigned Nurses"
								value={jobs.list.filter((job) => (job.nurse ? false : true)).length}
								isSmallerDevice={isSmallerDevice}
							/>
							{/* Display count of jobs with status 0 (pending) */}
							<QuickAccessItem
								title="Job Request"
								value={jobs.list.filter((job) => job.status === 0).length}
								isSmallerDevice={isSmallerDevice}
							/>
						</div>
					</div>

					<section className={`${classes['dashboard-section']} ${classes['dashboard-dispatchRequests']}`}>
						<div className={classes['dashboard-section-header']}>
							<Typography className={classes['dashboard-section-header-title']} variant="h4">
								Job Request
							</Typography>
							<Button
								className={classes['dashboard-section-header-button']}
								title="See all requests"
								color="primary"
								sizing="small"
								end={<Image src={'icRightCaretPrimary'} />}
								style={{
									marginTop: '-0.2rem',
								}}
								onClick={() => push('/jobs?filter=pending')}
							/>
						</div>
						{/* Display only pending jobs (status 0), limited to 10 items */}
						<JobRequestTable jobs={jobs.list.filter((job) => job.status === 0).slice(0, 10)} />
					</section>

					<section
						style={!mapToggleVisible ? { flexBasis: '100%' } : {}}
						className={`${classes['dashboard-section']} ${classes['dashboard-activeDispatches']}`}
					>
						<div className={classes['dashboard-section-header']}>
							<Typography className={classes['dashboard-section-header-title']} variant="h4">
								Active Jobs
							</Typography>
							<Button
								className={classes['dashboard-section-header-button']}
								title="See all active jobs"
								color="primary"
								sizing="small"
								end={<Image src={'icRightCaretPrimary'} />}
								onClick={() => push('/jobs?filter=active')}
							/>
						</div>
						{/* Display only active jobs, limited to 10 items */}
						<ActiveJobsTable
							jobs={jobs.list.filter((job) => JobStatus.isActive(job.status)).slice(0, 10)}
						/>
					</section>
					<div className="dashboard-activeDispatches"></div>
				</div>
			)}
		</div>
	);
};

export default DashboardScreen;
